/**
 * @spec docs/superpowers/specs/2026-10-15-staking-ui-wallet-actions-design.md §4.2
 * @standard docs/standards/staking-ui-wallet-actions.md#decide
 *
 * The one place that decides what a wallet must do before an action. Pure: no React, no I/O.
 *
 * - Embedded chain-signatures are `none`: Privy's handleSignedTypedData signs any domain without
 *   comparing chains (verified in Privy 1.99.1), so the embedded wallet changes chain only to send
 *   a transaction.
 * - WalletConnect signatures only warn on a mismatch: Ledger Live signs Base payloads while its
 *   session reports another chain (e644855d).
 * - An unreadable chain (null) never blocks here; prepareWallet refuses an external transaction
 *   whose chain it cannot read.
 */
import type { WalletKind } from '@/lib/wallet-kind';
import type { Decision, WalletAction } from './types';

export function decide(
  walletKind: WalletKind,
  currentChainId: number | null,
  action: WalletAction
): Decision {
  const differs = currentChainId !== null && currentChainId !== action.chainId;
  switch (action.kind) {
    case 'chainless-signature':
      return 'none';
    case 'raw-signature':
      return walletKind === 'embedded' ? 'none' : 'block-unsupported';
    case 'chain-signature':
      if (walletKind === 'embedded' || !differs) return 'none';
      return walletKind === 'wallet_connect' ? 'warn' : 'ask';
    case 'transaction':
      if (walletKind === 'embedded') return 'switch-silently';
      return differs ? 'ask' : 'none';
  }
}
